import { LifePart } from '../../state/LifePart';
import { WheelSnapshot } from '../../state/WheelSnapshot';
import { format } from 'date-fns';

/**
 * Создаёт снимок колеса баланса - сохраняем все данные частей жизни
 * Один снимок в сутки
 */
export function createWheelSnapshot(
    wheelId: string,
    lifeParts: LifePart[]
): WheelSnapshot {
    const now = new Date();
    
    return {
        uuid: crypto.randomUUID(),
        wheelId,
        timestamp: Date.now(),
        date: format(now, 'yyyy-MM-dd'),
        lifeParts: lifeParts.map(lp => ({
            uuid: lp.uuid,
            name: lp.name || 'Без названия',
            color: lp.color,
            description: lp.description,
            startpoint: lp.startpoint || 1,
            primary: lp.primary || false,
            order: lp.order || 0,
        })),
    };
}

/**
 * Проверяет, был ли уже снимок сегодня
 */
export function hasSnapshotToday(snapshots: WheelSnapshot[]): boolean {
    const today = format(new Date(), 'yyyy-MM-dd');
    return snapshots.some(s => s.date === today);
}

/**
 * Находит снимки колеса на указанные даты
 * Снимок для даты = снимок этой даты ИЛИ ближайший более ранний
 * (как выглядело колесо на эту дату)
 * 
 * Начальный снимок: максимальная дата <= dateFrom (как выглядело в начало периода)
 * Конечный снимок: максимальная дата <= dateTo (как выглядело в конец периода)
 * 
 * Если начальный и конечный снимки оказываются одинаковыми,
 * то начальный считаем отсутствующим (null), т.к. нет данных "до" периода.
 */
export function findWheelSnapshotsForPeriod(
    snapshots: WheelSnapshot[],
    dateFrom: Date,
    dateTo: Date
): { initial: WheelSnapshot | null; final: WheelSnapshot | null } {
    const fromDateStr = format(dateFrom, 'yyyy-MM-dd');
    const toDateStr = format(dateTo, 'yyyy-MM-dd');
    
    // Сортируем снимки по дате (возрастание - старые сначала)
    const sorted = [...snapshots].sort((a, b) => a.date.localeCompare(b.date));
    
    // Находим начальный снимок: максимальная дата <= dateFrom
    let initialSnapshot: WheelSnapshot | null = null;
    for (let i = sorted.length - 1; i >= 0; i--) {
        if (sorted[i].date <= fromDateStr) {
            initialSnapshot = sorted[i];
            break;
        }
    }
    
    // Находим конечный снимок: максимальная дата <= dateTo
    let finalSnapshot: WheelSnapshot | null = null;
    for (let i = sorted.length - 1; i >= 0; i--) {
        if (sorted[i].date <= toDateStr) {
            finalSnapshot = sorted[i];
            break;
        }
    }
    
    // Если начальный и конечный снимки - это один и тот же снимок,
    // значит нет данных "до" периода, обнуляем начальный
    if (initialSnapshot && finalSnapshot && initialSnapshot.uuid === finalSnapshot.uuid) {
        initialSnapshot = null;
    }
    
    return { initial: initialSnapshot, final: finalSnapshot };
}

/**
 * Извлекает данные для отрисовки колеса из снимка
 * Преобразует в формат LifePart для компонента Chart
 */
export interface WheelBalanceData {
    uuid: string;
    name: string;
    color: string;
    startpoint: number;
    primary: boolean;
}

export function extractWheelBalanceData(snapshot: WheelSnapshot | null): WheelBalanceData[] {
    if (!snapshot) {
        return [];
    }
    
    return snapshot.lifeParts.map(p => ({
        uuid: p.uuid,
        name: p.name || 'Без названия',
        color: p.color,
        startpoint: p.startpoint || 1,
        primary: p.primary || false,
    }));
}
